/**
 * Microphone access — AUDIO ONLY.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * This is the ONLY module in the frontend permitted to call getUserMedia.
 * Keeping the call in one place is what makes the project's no-camera rule
 * checkable: `tests/test_project_constraints.py` asserts that every
 * getUserMedia call in the frontend disables video explicitly, and that no
 * file anywhere in the frontend requests a camera track.
 *
 * NS Sales Bot is a voice product. It must never request a camera, and the
 * constraint below must never grow a `video` track. Camera access is also
 * refused server-side: the LiveKit token grants `canPublishSources:
 * ["microphone"]`, so a camera track would be rejected even if this file
 * were changed.
 * ─────────────────────────────────────────────────────────────────────────
 */

/** Constraints for a voice session. `video: false` is explicit, not implied. */
export const AUDIO_ONLY_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
  video: false,
};

/** Human-readable reasons a microphone request can fail. */
export const MIC_ERRORS = {
  NotAllowedError:
    'Microphone access was blocked. Allow the microphone for this site in your browser, then try again.',
  NotFoundError: 'No microphone was found. Connect one and try again.',
  NotReadableError:
    'The microphone is in use by another application. Close it and try again.',
  SecurityError:
    'Microphone access needs a secure context. Use http://localhost or an https:// address.',
};

/**
 * Ask for microphone permission.
 *
 * The returned tracks are stopped immediately: this call exists to trigger
 * the browser permission prompt and confirm a working input device. LiveKit
 * opens its own track once the room is joined.
 *
 * @returns {Promise<void>} resolves when permission is granted
 * @throws {Error} with a readable `message` when permission is refused
 */
export async function requestMicrophoneAccess() {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error(
      'This browser does not support microphone capture. Try a recent Chrome, Edge, or Firefox.',
    );
  }

  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia(AUDIO_ONLY_CONSTRAINTS);
  } catch (cause) {
    const message = MIC_ERRORS[cause?.name] || `Could not access the microphone (${cause?.name || 'unknown error'}).`;
    const error = new Error(message);
    error.name = cause?.name || 'MicrophoneError';
    throw error;
  }

  // Release the device; LiveKit publishes its own track after connecting.
  stream.getTracks().forEach((track) => track.stop());
}
